/**
 * useHospitalData - Hook for hospital data management
 *
 * Manages:
 * - Hospital list fetching
 * - Hospital selection
 * - Locais de contagem
 * - Representantes
 */
import { useState, useEffect, useMemo } from 'react';
import apiClient from '../services/apiClient';
import useAuthStore from '../stores/authStore';
import { cnpjMatch } from '../utils/cnpjMatch';  // Story 2.38
import { sortByAlpha } from '../utils/sortAlpha';  // Story 2.51

// NOTE: apiClient already has baseURL with /api, so we use relative paths

export interface Hospital {
  id: number;
  codigo?: string | null;  // Campo pode ser null no banco (nullable)
  nome?: string | null;    // Campo legado - usar nome_fantasia
  cliente_razao_social?: string | null;
  cliente_nome_fantasia?: string | null;
  pessoa: string;
  razao_social?: string | null;
  nome_fantasia: string;   // Obrigatório no modelo
  cnpj?: string | null;
  endereco?: string | null;
  bairro?: string | null;
  cidade?: string | null;
  estado?: string | null;
  cep?: string | null;
  telefone?: string | null;
  ramal?: string | null;
  email?: string | null;
  periodicidade?: string | null;
  observacoes?: string | null;
  ativo: boolean;
}

export interface LocalContagem {
  id: number;
  local: string;
  responsavel: string;
  telefone?: string;
  ramal?: string;
  email?: string;
}

export interface Representante {
  id: number;
  nome: string;
  telefone?: string;
  email?: string;
  cliente_razao_social?: string;
  cliente_nome_fantasia?: string;
  celular?: string;
}

export interface HospitalAuditor {
  id: number;
  auditor_id: number;
  auditor_nome: string;
  auditor_email: string;
  rota: string;
  unidade_responsavel: string;
  principal: boolean;
  indisponivel_inicio: string | null;
  indisponivel_fim: string | null;
  indisponivel_motivo: string;
  suplente_id: number | null;
  suplente_nome: string;
  is_indisponivel: boolean;
}

export function useHospitalData() {
  const token = useAuthStore((state) => state.token);
  const user = useAuthStore((state) => state.user);
  // Story 2.62: somente MASTER vê/reativa estabelecimentos inativos
  const isMaster = user?.role === 'MASTER';

  // Hospital list state
  const [hospitais, setHospitais] = useState<Hospital[]>([]);
  const [selectedHospital, setSelectedHospital] = useState<Hospital | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  // Story 2.62: toggle "Mostrar inativos" (só MASTER). Quando MASTER, buscamos
  // ativos + inativos do backend (incluir_inativos) e filtramos client-side aqui.
  const [showInactive, setShowInactive] = useState(false);

  // Related data
  const [locaisContagem, setLocaisContagem] = useState<LocalContagem[]>([]);
  const [representantes, setRepresentantes] = useState<Representante[]>([]);

  // Fetch hospitals
  useEffect(() => {
    const fetchHospitais = async () => {
      try {
        setLoading(true);
        // Story 2.62: MASTER busca ativos + inativos (filtro de exibição é client-side)
        const response = await apiClient.get(`/hospitais`, {
          params: isMaster ? { incluir_inativos: true } : {},
        });
        const rawData = response.data.items || response.data;
        // Story 2.51: ordenar alfabético PT-BR — afeta todos os comboboxes
        // que consomem este hook (AgendamentoTab combobox, HospitalHeader,
        // filtros, etc).
        const hospitaisData = sortByAlpha(rawData, ['nome_fantasia', 'razao_social']);
        setHospitais(hospitaisData);
        if (hospitaisData.length > 0) {
          setSelectedHospital(hospitaisData[0]);
        }
        setError('');
      } catch (err: any) {
        console.error('Erro ao buscar hospitais:', err);
        setError('Erro ao carregar hospitais');
      } finally {
        setLoading(false);
      }
    };

    fetchHospitais();
  }, [token, isMaster]);

  // Fetch locais and representantes when hospital is selected
  useEffect(() => {
    const fetchLocais = async () => {
      if (!selectedHospital) return;
      try {
        const response = await apiClient.get(
          `/hospitais/${selectedHospital.id}/locais`
        );
        setLocaisContagem(response.data);
      } catch (err) {
        console.error('Erro ao buscar locais:', err);
      }
    };

    const fetchRepresentantes = async () => {
      if (!selectedHospital) return;
      try {
        const response = await apiClient.get(
          `/hospitais/${selectedHospital.id}/representantes`
        );
        setRepresentantes(response.data);
      } catch (err) {
        console.error('Erro ao buscar representantes:', err);
      }
    };

    fetchLocais();
    fetchRepresentantes();
  }, [selectedHospital, token]);

  // Filtered hospitals based on search term (defensive coding for nullable fields)
  const filteredHospitais = useMemo(() => {
    // Story 2.62: por padrão só ativos; MASTER pode ver inativos via toggle.
    // (não-MASTER já recebe só ativos do backend, mas filtramos por segurança)
    let base = hospitais;
    if (!showInactive) {
      base = base.filter(h => h.ativo);
    }

    if (!searchTerm.trim()) return base;

    const term = searchTerm.toLowerCase();
    return base.filter(h => {
      const clienteDisplay = h.cliente_nome_fantasia || h.cliente_razao_social || '';
      return (
        (h.nome_fantasia?.toLowerCase().includes(term)) ||
        (h.codigo?.toLowerCase().includes(term)) ||
        (clienteDisplay.toLowerCase().includes(term)) ||
        // Story 2.38: busca por CNPJ normalizada (ignora ./- /)
        cnpjMatch(h.cnpj, searchTerm) ||
        (h.cidade?.toLowerCase().includes(term)) ||
        (h.estado?.toLowerCase().includes(term)) ||
        (h.razao_social?.toLowerCase().includes(term))
      );
    });
  }, [hospitais, searchTerm, showInactive]);

  // Refresh hospital list
  const refreshHospitais = async () => {
    try {
      const response = await apiClient.get(`/hospitais`, {
        params: isMaster ? { incluir_inativos: true } : {},
      });
      const rawData = response.data.items || response.data;
      setHospitais(sortByAlpha(rawData, ['nome_fantasia', 'razao_social']));
    } catch (err) {
      console.error('Erro ao atualizar hospitais:', err);
    }
  };

  // Story 2.62: reativar estabelecimento desativado (somente MASTER)
  const reactivateHospital = async (hospitalId: number) => {
    const response = await apiClient.post(`/hospitais/${hospitalId}/reativar`);
    // Atualiza o item na lista para ativo=true (mantém seleção/ordem)
    setHospitais(prev => prev.map(h => h.id === hospitalId ? { ...h, ativo: true } : h));
    setSelectedHospital(prev =>
      prev && prev.id === hospitalId ? { ...prev, ativo: true } : prev
    );
    return response.data;
  };

  // Refresh locais
  const refreshLocais = async () => {
    if (!selectedHospital) return;
    try {
      const response = await apiClient.get(
        `/hospitais/${selectedHospital.id}/locais`
      );
      setLocaisContagem(response.data);
    } catch (err) {
      console.error('Erro ao atualizar locais:', err);
    }
  };

  // Refresh representantes
  const refreshRepresentantes = async () => {
    if (!selectedHospital) return;
    try {
      const response = await apiClient.get(
        `/hospitais/${selectedHospital.id}/representantes`
      );
      setRepresentantes(response.data);
    } catch (err) {
      console.error('Erro ao atualizar representantes:', err);
    }
  };

  // Update hospital in list
  const updateHospitalInList = (hospital: Hospital) => {
    setHospitais(prev => prev.map(h => h.id === hospital.id ? hospital : h));
    if (selectedHospital?.id === hospital.id) {
      setSelectedHospital(hospital);
    }
  };

  // Add hospital to list
  const addHospitalToList = (hospital: Hospital) => {
    setHospitais(prev => [hospital, ...prev]);
    setSelectedHospital(hospital);
  };

  // Remove hospital from list
  const removeHospitalFromList = (hospitalId: number) => {
    setHospitais(prev => prev.filter(h => h.id !== hospitalId));
    if (selectedHospital?.id === hospitalId) {
      setSelectedHospital(null);
    }
  };

  return {
    // Hospital data
    hospitais,
    selectedHospital,
    setSelectedHospital,
    filteredHospitais,
    loading,
    error,
    searchTerm,
    setSearchTerm,

    // Story 2.62: inativos (MASTER)
    isMaster,
    showInactive,
    setShowInactive,
    reactivateHospital,

    // Related data
    locaisContagem,
    setLocaisContagem,
    representantes,
    setRepresentantes,

    // Refresh functions
    refreshHospitais,
    refreshLocais,
    refreshRepresentantes,

    // List manipulation
    updateHospitalInList,
    addHospitalToList,
    removeHospitalFromList,

    // Token for API calls
    token,
  };
}
